"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  type EssayType,
  TITLE_FRAMES,
  titlesFor,
} from "@/lib/tool-kit/essay/titles";

function shuffle<T>(items: T[]): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** English strings. A translated content file overrides these through `ui`. */
export const ESSAY_TITLE_GENERATOR_STRINGS = {
  argumentative: "Argumentative",
  analytical: "Analytical",
  expository: "Expository",
  compareAndContrast: "Compare & contrast",
  yourEssayTopic: "Your essay topic",
  topicPlaceholder: "e.g. school uniforms, remote work, renewable energy",
  essayType: "Essay type",
  regenerateTitles: "Regenerate Titles",
  generateTitles: "Generate Titles",
  titleOptionsHeading:
    "Title options — regenerate to reshuffle, then tighten the wording to match your thesis",
  copied: "Copied!",
  copy: "Copy",
  titleFrames: TITLE_FRAMES,
} as const;

export default function EssayTitleGeneratorWidget() {
  const [topic, setTopic] = useState("");
  const [essayType, setEssayType] = useState<EssayType>("argumentative");
  const [titles, setTitles] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const strings = useToolUi(ESSAY_TITLE_GENERATOR_STRINGS);
  const locale = useToolLocale();

  const essayTypes: { value: EssayType; label: string }[] = [
    { value: "argumentative", label: strings.argumentative },
    { value: "analytical", label: strings.analytical },
    { value: "expository", label: strings.expository },
    { value: "compare", label: strings.compareAndContrast },
  ];

  function handleGenerate() {
    const trimmed = topic.trim();
    if (trimmed.length === 0) {
      return;
    }
    setTitles(
      shuffle(
        titlesFor(trimmed, essayType, strings.titleFrames[essayType], locale)
      )
    );
    setCopiedIndex(null);
  }

  function handleCopy(title: string, index: number) {
    writeToClipboard(title).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  return (
    <>
      {/* Topic */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="title-topic"
        >
          {strings.yourEssayTopic}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="title-topic"
          onChange={(e) => setTopic(e.target.value)}
          placeholder={strings.topicPlaceholder}
          type="text"
          value={topic}
        />
      </div>

      {/* Essay type */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.essayType}
        </p>
        <div className="flex flex-wrap gap-3">
          {essayTypes.map((t) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={t.value}
            >
              <input
                checked={essayType === t.value}
                className="accent-primary"
                name="essay-type"
                onChange={() => setEssayType(t.value)}
                type="radio"
                value={t.value}
              />
              {t.label}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={topic.trim().length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {titles.length > 0
            ? strings.regenerateTitles
            : strings.generateTitles}
        </button>
      </div>

      {/* Results */}
      {titles.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.titleOptionsHeading}
          </h2>
          <ul className="space-y-3">
            {titles.map((title, index) => (
              <li
                className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-5"
                key={title}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {title}
                </p>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                  onClick={() => handleCopy(title, index)}
                  type="button"
                >
                  {copiedIndex === index ? strings.copied : strings.copy}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
